import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { isDefined } from '@fi-shared/utils';
import {
    IOwnershipArea,
    IOwnershipBusinessUnit,
    IOwnershipUser,
} from '@fi-app/other-settings/transfer-ownership/fi-transfer-ownership';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { AppTextService } from '@fi-shared/app-text';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
    FiDropdownItemAdditionalTextFn,
    FiDropdownItemDisabledFn,
    FiDropdownItemProfileDataFn,
    ISelectHierarchyItem,
} from '@fi-shared/fi-select-search/fi-select-search';
import { FiTransferOwnershipHelperService } from '@fi-app/other-settings/transfer-ownership/fi-transfer-ownership-helper.service';
import { fiItemsFlatten } from '@fi-shared/utils/fi-items-flatten';
import { ObjectTypes } from '@fi-shared/types';
import { IUserProfile } from '@fi-shared/profile-icon/profile-icon.component';

@Component({
    selector: 'fi-transfer-ownership-user-select',
    template: `
        <fi-foldable-select-item-search
            [(ngModel)]="selectedUserId"
            (ngModelChange)="doPropagateChanges()"
            [items]="ownershipSelectData"
            [label]="selectLabel"
            [disabled]="isDisabled"
            [isOptionDisabledFn]="getOptionDisabledFn"
            [isCustomMatSelectTrigger]="true"
            [getUserProfileFn]="getUserProfileFn"
            [getOptionAdditionalTextFn]="getOptionAdditionalTextFn"
            [getOptionAdditionalClassFn]="getOptionAdditionalClassFn"
        >
            <span class="mat-select-trigger-content">{{ getSelectedUserData() }}</span>
        </fi-foldable-select-item-search>
    `,
    styleUrl: './fi-transfer-ownership-user-select.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-transfer-ownership-user-select' },
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiTransferOwnershipUserSelectComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiTransferOwnershipUserSelectComponent,
        },
    ],
})
export class FiTransferOwnershipUserSelectComponent
    extends ControlValueAccessorValidatorBase<IOwnershipUser>
    implements OnInit, OnChanges
{
    @Input({ required: true }) ownershipAreas: IOwnershipArea[] = [];
    @Input({ required: true }) selectLabel: string;
    @Input() isUserDisabledFn: FiDropdownItemDisabledFn<IOwnershipUser> = (
        item: IOwnershipUser,
        allItems: IOwnershipUser[],
    ) => {
        return item.IsDisabled;
    };
    @Input() isDisabled: boolean;
    selectedUserId: number;
    availableAreas: IOwnershipArea[] = [];
    availableUsers: IOwnershipUser[] = [];
    availableUnits: IOwnershipBusinessUnit[] = [];

    constructor(
        injector: Injector,
        private appTextService: AppTextService,
        private fiTransferOwnershipHelperService: FiTransferOwnershipHelperService,
    ) {
        super(injector);
    }

    ownershipSelectData: ISelectHierarchyItem[] = [];
    expandedItems: ISelectHierarchyItem[] = [];

    ngOnInit() {
        this.availableAreas = fiItemsFlatten(this.ownershipAreas);
        this.availableUnits = this.availableAreas.flatMap((area: IOwnershipArea) => area.BusinessUnits);
        this.availableUsers = this.availableUnits.flatMap((unit: IOwnershipBusinessUnit) => unit.Users);

        if (isDefined(this.ngModel)) {
            this.selectedUserId = this.ngModel.Id;
        }

        this.ownershipSelectData = this.fiTransferOwnershipHelperService.mapOwnershipHierarchy(this.ownershipAreas);
        this.expandedItems = fiItemsFlatten(this.ownershipSelectData).filter(
            (item: ISelectHierarchyItem) => item.ChildItems?.length,
        );
        this.doPropagateChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes.ngModel;
        if (change) {
            this.selectedUserId = isDefined(this.ngModel) ? this.ngModel.Id : undefined;
        }
    }

    doPropagateChanges() {
        this.ngModel = this.availableUsers.find((item) => item.Id == this.selectedUserId);
        super.doPropagateChanges();
    }

    private isHierarchyItemUser(item: ISelectHierarchyItem): boolean {
        return item.ObjectType === ObjectTypes.User;
    }

    private isHierarchyItemUnit(item: ISelectHierarchyItem): boolean {
        return item.ObjectType === ObjectTypes.BusinessUnit;
    }

    private isHierarchyItemArea(item: ISelectHierarchyItem): boolean {
        return item.ObjectType === ObjectTypes.Area;
    }

    private getOwnershipUserById(id: number): IOwnershipUser {
        return this.availableUsers.find((user) => user.Id === id);
    }

    getOwnershipItemByHierarchyItem(item: ISelectHierarchyItem) {
        let ownershipItem: IOwnershipUser | IOwnershipBusinessUnit | IOwnershipArea;
        if (this.isHierarchyItemUser(item)) {
            ownershipItem = this.availableUsers.find((user: IOwnershipUser) => user.Id === item.Id);
        } else if (this.isHierarchyItemUnit(item)) {
            ownershipItem = this.availableUnits.find((unit: IOwnershipBusinessUnit) => unit.Id === item.Id);
        } else if (this.isHierarchyItemArea(item)) {
            ownershipItem = this.availableAreas.find((area: IOwnershipArea) => area.Id === item.Id);
        }
        if (!isDefined(ownershipItem)) {
            throw new Error(`unknown ownership item in getOwnershipItemByHierarchyItem ${item.ObjectType}`);
        }
        return ownershipItem;
    }

    getUserProfileFn: FiDropdownItemProfileDataFn<ISelectHierarchyItem> = (
        item: ISelectHierarchyItem,
        allItems: ISelectHierarchyItem[],
    ): IUserProfile => {
        if (!this.isHierarchyItemUser(item)) return;
        return this.getOwnershipUserById(item.Id);
    };

    getOptionAdditionalTextFn: FiDropdownItemAdditionalTextFn<ISelectHierarchyItem> = (
        item: ISelectHierarchyItem,
        allItems: ISelectHierarchyItem[],
    ): string => {
        if (this.isHierarchyItemArea(item)) return;

        const ownershipItemByHierarchyItem = this.getOwnershipItemByHierarchyItem(item);
        if ('IsActivated' in ownershipItemByHierarchyItem && !ownershipItemByHierarchyItem.IsActivated) {
            return this.appTextService.get('common.deactivated');
        }
    };

    getOptionAdditionalClassFn: FiDropdownItemAdditionalTextFn<ISelectHierarchyItem> = (
        item: ISelectHierarchyItem,
        allItems: ISelectHierarchyItem[],
    ): string => {
        if (this.isHierarchyItemArea(item)) return;

        const ownershipItemByHierarchyItem = this.getOwnershipItemByHierarchyItem(item);
        if ('IsActivated' in ownershipItemByHierarchyItem && !ownershipItemByHierarchyItem.IsActivated) {
            return 'red-color';
        }
    };

    getOptionDisabledFn: FiDropdownItemDisabledFn<ISelectHierarchyItem> = (
        item: ISelectHierarchyItem,
        allItems: ISelectHierarchyItem[],
    ) => {
        if (item.ObjectType === ObjectTypes.User) {
            const user: IOwnershipUser = this.availableUsers.find((user) => user.Id === item.Id);
            return this.isUserDisabledFn(user, this.availableUsers);
        }

        return false;
    };

    getSelectedUserData(): string {
        if (!isDefined(this.selectedUserId)) return;

        const { PreferredBusinessUnitName, AreaName, FullName } = this.availableUsers.find(
            (user: IOwnershipUser) => user.Id === this.selectedUserId,
        );

        return this.appTextService.get('ownership.transfer_selected_user', {
            '{0}': FullName,
            '{1}': AreaName,
            '{2}': PreferredBusinessUnitName,
        });
    }

    protected readonly ObjectTypes = ObjectTypes;
}
